import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import { Close as CloseIcon, AddCircleOutline, ErrorOutline } from "@mui/icons-material";
import { createTemplate } from "../../api/services/scheduling";
import { useSnackbar } from "notistack";
import { useQueryClient } from "react-query";
import { QK } from "../../base/qk";

const TemplateCreateModal = ({ show, handleClose, sublocationId }) => {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const isSublocationMissing = !sublocationId;

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (isSublocationMissing) {
      enqueueSnackbar("Please select an area/sublocation first", { variant: "error" });
      return;
    }

    const formData = {
      name,
      sublocation: sublocationId,
    };

    setLoading(true);

    try {
      const response = await createTemplate(formData);
      if (response.status === 201) {
        enqueueSnackbar("Template created successfully", { variant: "success" });
        queryClient.invalidateQueries(QK.templates);
        setName("");
        handleClose();
      }
    } catch (err) {
      enqueueSnackbar("Failed to create template", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={show} 
      onClose={handleClose} 
      fullWidth 
      maxWidth="xs"
      PaperProps={{
        sx: { borderRadius: "12px" }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <AddCircleOutline color={isSublocationMissing ? "disabled" : "primary"} />
        <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
          Create New Template
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 16, top: 16, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 3 }}>
          {/* Industry Standard Restriction Message */}
          {isSublocationMissing ? (
            <Alert 
              severity="warning" 
              icon={<ErrorOutline fontSize="inherit" />}
              sx={{ mb: 3, borderRadius: "8px", fontWeight: 500 }}
            >
              Selection Required: Please select an <strong>Area</strong> from the main dashboard before creating a template.
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>
              Specify a template name for the selected area to organize your runs efficiently.
            </Typography>
          )}

          <TextField
            autoFocus
            margin="dense"
            label="Template Name"
            type="text"
            fullWidth
            variant="outlined"
            placeholder={isSublocationMissing ? "Select Area first..." : "e.g. Morning Shift Template"}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={loading || isSublocationMissing} // সাবলোকেশন না থাকলে ইনপুটও ডিজেবল
            required
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: "8px" }
            }}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2, px: 3, bgcolor: "#fcfcfc" }}>
          <Button 
            onClick={handleClose} 
            color="inherit" 
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || isSublocationMissing || !name.trim()}
            sx={{ 
              textTransform: "none", 
              fontWeight: 700, 
              borderRadius: "8px",
              px: 3,
              boxShadow: "none",
              "&:disabled": {
                bgcolor: "#e0e0e0",
                color: "#9e9e9e"
              }
            }}
            startIcon={loading && <CircularProgress size={16} color="inherit" />}
          >
            {isSublocationMissing ? "Selection Missing" : loading ? "Creating..." : "Create Template"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default TemplateCreateModal;